import { useParams, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { JsonLd, getBreadcrumbSchema, getWebPageSchema } from '@/components/feature/JsonLd';
import { videos, categories } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = categories.find((c) => c.slug === slug);
  const categoryName = category?.name || slug;
  const categoryVideos = slug === 'all'
    ? videos
    : slug === 'hot'
      ? videos.filter((v) => v.isHot)
      : videos.filter((v) => v.categorySlug === slug);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryName },
  ];

  const pageTitle = `${categoryName} - 桃花视频 | 高清${categoryName}在线观看`;
  const pageDesc = `桃花视频${categoryName}频道，提供最新最全的高清${categoryName}资源，免费在线观看，流畅不缓冲，持续更新中。`;

  return (
    <>
      <ScrollToTop />
      <JsonLd data={getWebPageSchema(pageTitle, pageDesc, `/category/${slug}`)} />
      <JsonLd data={getBreadcrumbSchema([
        { name: '桃花视频', item: '/' },
        { name: categoryName, item: `/category/${slug}` },
      ])} />

      <Header />

      <main className="pt-16">
        <div className="bg-background-100">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        <section className="w-full px-4 md:px-6 py-6 md:py-8 bg-background-100" aria-label={`${categoryName}视频列表`}>
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
              {categoryName}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              共 <strong className="text-primary-500">{categoryVideos.length}</strong> 部作品
              {category?.name !== '全部' && ` · ${categoryName}频道持续更新中`}
            </p>
          </div>

          <div className="flex items-center gap-1 overflow-x-auto pb-3 mb-6 scrollbar-hide">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`shrink-0 px-5 py-2.5 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-50 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {categoryVideos.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {categoryVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">暂无相关内容</p>
              <Link
                to="/"
                className="inline-block mt-4 text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer"
              >
                返回首页
              </Link>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
}